import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_AVAILABLE_APPOINTMENTS } from '../graphql/queries';
import { Service } from '../types';
import { TimeSlot } from '../utils/timeSlotUtils';

/**
 * Convert minutes since midnight to HH:MM format
 * @param minutes - Number of minutes since midnight (0-1439)
 * @returns Time string in HH:MM format
 */
const minutesToTimeString = (minutes: number): string => {
  // Ensure minutes is within valid range
  if (minutes < 0 || minutes >= 1440) {
    console.warn(`Invalid minutes value: ${minutes}. Must be between 0 and 1439.`);
    return '00:00';
  }
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

interface UseAvailableTimeSlotsProps {
  storeId: string;
  selectedStaffId: string;
  selectedService: Service | null;
  selectedDate: string;
}

interface UseAvailableTimeSlotsReturn {
  timeSlots: TimeSlot[];
  loading: boolean;
  error: any;
  refetch: () => void;
}

export const useAvailableTimeSlots = ({
  storeId,
  selectedStaffId,
  selectedService,
  selectedDate
}: UseAvailableTimeSlotsProps): UseAvailableTimeSlotsReturn => {
  
  // Get available time slots directly from the backend
  const { 
    data: availableData, 
    loading, 
    error,
    refetch: refetchAvailable
  } = useQuery(GET_AVAILABLE_APPOINTMENTS, {
    variables: { 
      storeId,
      date: selectedDate,
      services: selectedService ? [selectedService.id] : undefined,
      staffId: selectedStaffId || undefined
    },
    skip: !storeId || !selectedDate || !selectedStaffId,
    fetchPolicy: 'cache-and-network', // Always fetch fresh data for availability
    errorPolicy: 'all' // Return partial data on error
  });

  // Transform backend response to TimeSlot format
  const timeSlots = useMemo(() => {
    if (!availableData?.availableAppointments) {
      return [];
    }

    const availableAppointments = availableData.availableAppointments;
    
    // Backend returns an array of minutes since midnight
    if (Array.isArray(availableAppointments)) {
      const validSlots = availableAppointments
        .filter(minutes => {
          if (typeof minutes !== 'number') {
            console.warn('Non-numeric value in availableAppointments:', minutes);
            return false;
          }
          if (minutes < 0 || minutes >= 1440) {
            console.warn('Invalid minutes value (out of range):', minutes);
            return false;
          }
          return true;
        })
        .map((minutes: number): TimeSlot => ({
          time: minutesToTimeString(minutes),
          disabled: false,
          reason: undefined
        }))
        .sort((a, b) => a.time.localeCompare(b.time)); // Sort chronologically

      return validSlots;
    }

    // If it's not an array, log the format for debugging
    console.warn('Unexpected availableAppointments format (not an array):', availableAppointments);
    return [];
  }, [availableData]);

  const refetch = useCallback(() => {
    refetchAvailable();
  }, [refetchAvailable]);

  return {
    timeSlots,
    loading,
    error,
    refetch
  };
};
